import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';

import { PageContainer } from '../layout/PageContainer';
import SearchBar from '../searchbar/SearchBar';
import { getCarImages } from '../../domain/carImages';
import { MOBILE_BRANDS, MOBILE_MODELS } from '../../mappings/mobile_mappings';

interface FeaturedCar {
  make: string;
  model: string;
}

const FEATURED_CARS: FeaturedCar[] = [
  { make: 'Mercedes-Benz', model: 'C 63 AMG' },
  { make: 'BMW', model: 'M6' },
  { make: 'Audi', model: 'Rs4' },
];

const HOW_IT_WORKS = [
  {
    icon: SearchOutlinedIcon,
    title: 'Search a make & model',
    body: 'Pick any of the brands and models we track - from everyday hatchbacks to weekend performance cars.',
  },
  {
    icon: ShowChartIcon,
    title: 'See the real price trend',
    body: 'We track how the average asking price for that exact model has moved over time, not a single snapshot.',
  },
  {
    icon: CalculateOutlinedIcon,
    title: 'Estimate true ownership cost',
    body: 'Fuel, tax, and insurance estimates for the specific engine you pick, so the price tag is not the whole story.',
  },
];

const brandCount = Object.keys(MOBILE_BRANDS).length;
const modelCount = Object.values(MOBILE_MODELS).reduce((sum, models) => sum + Object.keys(models).length, 0);

const STATS = [
  { value: `${brandCount}`, label: 'brands tracked' },
  { value: `${modelCount.toLocaleString()}+`, label: 'models tracked' },
  { value: '12+', label: 'price snapshots per model' },
];

export function HomePage() {
  return (
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(180deg, #0f1b2d 0%, #16263f 100%)',
          color: 'common.white',
          pt: { xs: 8, sm: 12 },
          pb: { xs: 10, sm: 14 },
        }}
      >
        <PageContainer sx={{ py: 0 }}>
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h1" sx={{ fontSize: { xs: 36, sm: 56 }, maxWidth: 780 }}>
              Know the real market price before you buy.
            </Typography>
            <Typography variant="h6" fontWeight={400} sx={{ color: 'grey.300', maxWidth: 620 }}>
              Track historical pricing, compare engine specs, and estimate true ownership costs for any car on the
              market.
            </Typography>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <SearchBar />
            </Box>
            <Stack direction="row" spacing={{ xs: 3, sm: 6 }} sx={{ pt: 2 }}>
              {STATS.map((stat) => (
                <Box key={stat.label} textAlign="center">
                  <Typography variant="h4" color="secondary.main">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </PageContainer>
      </Box>

      <PageContainer>
        <Typography variant="h4" textAlign="center" gutterBottom>
          How it works
        </Typography>
        <Typography color="text.secondary" textAlign="center" sx={{ maxWidth: 560, mx: 'auto', mb: 5 }}>
          AutoAdviser is a research tool, not a dealership - we help you go into a negotiation with real data.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
          {HOW_IT_WORKS.map((step, index) => (
            <Card key={step.title} sx={{ p: 3 }}>
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'secondary.main',
                    color: 'secondary.contrastText',
                  }}
                >
                  <step.icon />
                </Box>
                <Typography variant="overline" color="text.secondary">
                  Step {index + 1}
                </Typography>
                <Typography variant="h6">{step.title}</Typography>
                <Typography color="text.secondary">{step.body}</Typography>
              </Stack>
            </Card>
          ))}
        </Box>
      </PageContainer>

      <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        <PageContainer>
          <Typography variant="h4" textAlign="center" gutterBottom>
            Most researched cars
          </Typography>
          <Typography color="text.secondary" textAlign="center" sx={{ mb: 5 }}>
            A few popular starting points, or search for your own above.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
            {FEATURED_CARS.map((car) => {
              const image = getCarImages(car.make, car.model)[0] ?? null;
              return (
                <Card key={`${car.make}-${car.model}`} sx={{ transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <CardActionArea
                    component={Link}
                    to={`/cars/${encodeURIComponent(car.make)}/${encodeURIComponent(car.model)}`}
                  >
                    {image && (
                      <Box
                        component="img"
                        src={image.thumbUrl}
                        alt={`${car.make} ${car.model}`}
                        sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    <Stack spacing={1.5} alignItems="flex-start" sx={{ p: 3 }}>
                      {!image && (
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: 'common.white',
                          }}
                        >
                          <DirectionsCarFilledIcon />
                        </Box>
                      )}
                      <Typography variant="h6">
                        {car.make} {car.model}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        View price history and engine specs
                      </Typography>
                    </Stack>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </PageContainer>
      </Box>

      <PageContainer sx={{ textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Ready to get started?
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Browse every brand and model we track.
        </Typography>
        <Button
          component={Link}
          to="/cars"
          variant="contained"
          color="secondary"
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          Explore cars
        </Button>

        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 6 }}>
          <ChatBubbleOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Questions?{' '}
            <a href="viber://chat/?number=%2B359883506455" style={{ color: 'inherit' }}>
              Chat with us on Viber
            </a>
          </Typography>
        </Stack>
      </PageContainer>
    </Box>
  );
}
